import { createSelector } from '@reduxjs/toolkit';
import { every, forEach, isEmpty, reduce } from 'lodash';

import { Status } from '@sonar/types';

import { getSelectedStoresIds as selectSelectedStoreIds } from '../Preferences';
import { EntitiesPerStoreState } from '../Reports/types';

import { compareCustomerTabCredits } from './helpers';
import { CustomerTabCreditInfo, RootStateWithCustomerTabCredits } from './types';

/**
 * Get all CustomerTabCredits organized per store
 */
const selectAllCustomerTabCreditsPerStore = ({
  CustomerTabCredits,
}: RootStateWithCustomerTabCredits): EntitiesPerStoreState<CustomerTabCreditInfo> =>
  CustomerTabCredits;

/**
 * Check if all CustomerTabCredits are loaded
 */
export const selectAreCustomerTabCreditsLoaded = createSelector(
  selectAllCustomerTabCreditsPerStore,
  CustomerTabCreditsPerStore =>
    !isEmpty(CustomerTabCreditsPerStore) &&
    every(
      CustomerTabCreditsPerStore,
      CustomerTabCredit =>
        CustomerTabCredit.status === Status.SUCCEEDED || CustomerTabCredit.status === Status.FAILED,
    ),
);

/**
 * Get all CustomerTabCredits organised by categories name
 */
export const selectAllCustomerTabCreditsInfo = createSelector(
  selectAllCustomerTabCreditsPerStore,
  selectSelectedStoreIds,
  (CustomerTabCreditsPerStore, storeIds: number[] | null) => {
    const allCustomerTabCredits = reduce(
      CustomerTabCreditsPerStore,
      (
        globalCustomerTabCredits: { [category: string]: CustomerTabCreditInfo },
        CustomerTabCredit,
        storeId,
      ) => {
        if (!storeIds?.includes(Number(storeId)) || CustomerTabCredit.status !== Status.SUCCEEDED) {
          return globalCustomerTabCredits;
        }

        forEach(CustomerTabCredit.entities, CustomerTabCreditDetail => {
          const category = CustomerTabCreditDetail.creditType;
          if (!globalCustomerTabCredits[category]) {
            globalCustomerTabCredits[category] = CustomerTabCreditDetail;
          } else {
            const globalCustomerTabCredit = globalCustomerTabCredits[category];
            globalCustomerTabCredits[category] = {
              ...globalCustomerTabCredit,
              sumCreditCount:
                globalCustomerTabCredit.sumCreditCount + CustomerTabCreditDetail.sumCreditCount,
            };
          }

          return globalCustomerTabCredits;
        });

        return globalCustomerTabCredits;
      },
      {},
    );

    return Object.values(allCustomerTabCredits).sort(compareCustomerTabCredits);
  },
);

export const selectTotalCustomerTabCredit = createSelector(
  selectAllCustomerTabCreditsInfo,
  customerTabCreditInfo =>
    customerTabCreditInfo.reduce((acc, curr) => acc + curr.sumCreditAmount, 0),
);
